import { z } from 'zod';
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const date = new Date(value + 'T00:00:00Z');
    return Number.isFinite(+date) && date.toISOString().slice(0, 10) === value;
}, 'Use a valid YYYY-MM-DD date');
export const BallCalendarInput = z.object({
    timeZone: z.literal('America/Toronto').default('America/Toronto'),
    observeWeekendHolidays: z.boolean().default(true),
    additionalHolidays: z.array(z.object({ date: calendarDate, name: z.string().trim().min(1).max(80) }).strict()).max(1000).default([]),
    excludedHolidayDates: z.array(calendarDate).max(1000).default([]),
}).strict().refine(value => new Set(value.additionalHolidays.map(h => h.date)).size === value.additionalHolidays.length, 'Each company holiday date must be unique');
export const DEFAULT_BALL_CALENDAR = {
    timeZone: 'America/Toronto', observeWeekendHolidays: true, additionalHolidays: [], excludedHolidayDates: [],
};
const DAY = 86400000;
const dateKey = (date) => date.toISOString().slice(0, 10);
const utc = (year, month, day) => new Date(Date.UTC(year, month - 1, day));
const shift = (date, days) => new Date(+date + days * DAY);
function nthMonday(year, month, n) {
    const first = utc(year, month, 1);
    return shift(first, (8 - first.getUTCDay()) % 7 + (n - 1) * 7);
}
function easterSunday(year) {
    // Gregorian computus; dates vary by year, so no annually expiring lookup table.
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const value = h + l - 7 * m + 114;
    return utc(year, Math.floor(value / 31), value % 31 + 1);
}
/** Ontario's nine public holidays. Weekday substitutes are an editable company
 * convention, not a claim that Ontario mandates these particular substitute dates.
 * Source: ontario.ca/document/your-guide-employment-standards-act-0/public-holidays */
export function ontarioHolidays(year, observeWeekendHolidays = true) {
    const may24 = utc(year, 5, 24);
    const dates = [
        [utc(year, 1, 1), "New Year's Day"], [nthMonday(year, 2, 3), 'Family Day'],
        [shift(easterSunday(year), -2), 'Good Friday'],
        [shift(may24, -((may24.getUTCDay() + 6) % 7)), 'Victoria Day'],
        [utc(year, 7, 1), 'Canada Day'], [nthMonday(year, 9, 1), 'Labour Day'],
        [nthMonday(year, 10, 2), 'Thanksgiving'], [utc(year, 12, 25), 'Christmas Day'],
        [utc(year, 12, 26), 'Boxing Day'],
    ];
    const holidays = dates.map(([date, name]) => ({ date: dateKey(date), name }));
    const taken = new Set(holidays.map(h => h.date));
    if (observeWeekendHolidays)
        for (const [date, name] of dates) {
            if (![0, 6].includes(date.getUTCDay()))
                continue;
            let observed = shift(date, 1);
            while ([0, 6].includes(observed.getUTCDay()) || taken.has(dateKey(observed)))
                observed = shift(observed, 1);
            holidays.push({ date: dateKey(observed), name: name + ' (observed)' });
            taken.add(dateKey(observed));
        }
    return holidays.sort((a, b) => a.date.localeCompare(b.date));
}
//# sourceMappingURL=ball-calendar.js.map